import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLeague } from '@/contexts/LeagueContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface WaiverClaimRow {
  id: string;
  league_id: string;
  team_id: string;
  player_id: string;
  drop_player_id: string | null;
  faab_bid: number;
  priority: number;
  status: string;
  processed_at: string | null;
  winning_bid: number | null;
  created_at: string;
  // Joined fields
  player_name?: string;
  player_position?: string;
  player_team?: string;
  drop_player_name?: string;
}

export const useWaiverClaims = () => {
  const { activeLeague, teams } = useLeague();
  const { user } = useAuth();
  const { toast } = useToast();

  const [pendingClaims, setPendingClaims] = useState<WaiverClaimRow[]>([]);
  const [claimHistory, setClaimHistory] = useState<WaiverClaimRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const myTeam = teams.find(t => t.user_id === user?.id);

  const fetchClaims = useCallback(async () => {
    if (!activeLeague || !myTeam) { setLoading(false); return; }

    // Fetch my pending claims
    const { data: pending } = await supabase
      .from('waiver_claims')
      .select('*')
      .eq('league_id', activeLeague.id)
      .eq('team_id', myTeam.id)
      .eq('status', 'pending')
      .order('priority', { ascending: true });

    // Fetch my claim history (last 20)
    const { data: history } = await supabase
      .from('waiver_claims')
      .select('*')
      .eq('league_id', activeLeague.id)
      .eq('team_id', myTeam.id)
      .in('status', ['won', 'outbid', 'invalid'])
      .order('processed_at', { ascending: false })
      .limit(20);

    // Enrich with player names
    const allPlayerIds = new Set<string>();
    for (const c of [...(pending || []), ...(history || [])]) {
      allPlayerIds.add(c.player_id);
      if (c.drop_player_id) allPlayerIds.add(c.drop_player_id);
    }

    const playerNames = new Map<string, { name: string; pos: string; team: string }>();
    if (allPlayerIds.size > 0) {
      const { data: players } = await supabase
        .from('players')
        .select('id, full_name, position_abbreviation, mlb_team_abbreviation')
        .in('id', [...allPlayerIds]);
      for (const p of players || []) {
        playerNames.set(p.id, {
          name: p.full_name,
          pos: p.position_abbreviation,
          team: p.mlb_team_abbreviation || 'FA',
        });
      }
    }

    const enrich = (claims: any[]): WaiverClaimRow[] =>
      (claims || []).map(c => ({
        ...c,
        player_name: playerNames.get(c.player_id)?.name || 'Unknown',
        player_position: playerNames.get(c.player_id)?.pos || '??',
        player_team: playerNames.get(c.player_id)?.team || 'FA',
        drop_player_name: c.drop_player_id ? playerNames.get(c.drop_player_id)?.name : null,
      }));

    setPendingClaims(enrich(pending || []));
    setClaimHistory(enrich(history || []));
    setLoading(false);
  }, [activeLeague, myTeam]);

  useEffect(() => { fetchClaims(); }, [fetchClaims]);

  const submitClaim = async (
    playerId: string,
    faabBid: number,
    dropPlayerId?: string,
    acquisitionLimitCheck?: { maxAcquisitions: number; acquisitionsUsed: number; acquisitionPeriod: string },
    waiverWireId?: string,
  ): Promise<boolean> => {
    if (!activeLeague || !myTeam) return false;

    // Check acquisition limit if provided
    if (acquisitionLimitCheck && acquisitionLimitCheck.maxAcquisitions > 0) {
      // Each pending claim could result in an acquisition, so count those too
      const projected = acquisitionLimitCheck.acquisitionsUsed + pendingClaims.length;
      if (projected >= acquisitionLimitCheck.maxAcquisitions) {
        toast({
          title: 'Acquisition limit reached',
          description: `You've used or queued ${projected} of ${acquisitionLimitCheck.maxAcquisitions} allowed acquisitions ${acquisitionLimitCheck.acquisitionPeriod === 'weekly' ? 'this week' : 'this season'}.`,
          variant: 'destructive',
        });
        return false;
      }
    }

    const faabRemaining = myTeam.faab_remaining ?? activeLeague.faab_budget ?? 0;
    if (faabBid > faabRemaining) {
      toast({ title: 'Insufficient FAAB', description: `You only have $${faabRemaining} remaining.`, variant: 'destructive' });
      return false;
    }

    setSubmitting(true);

    // Set priority to next in line
    const nextPriority = pendingClaims.length + 1;

    const { error } = await supabase
      .from('waiver_claims')
      .insert({
        league_id: activeLeague.id,
        team_id: myTeam.id,
        player_id: playerId,
        drop_player_id: dropPlayerId || null,
        faab_bid: faabBid,
        priority: nextPriority,
        ...(waiverWireId ? { waiver_wire_id: waiverWireId } : {}),
      });

    if (error) {
      toast({ title: 'Claim failed', description: error.message, variant: 'destructive' });
      setSubmitting(false);
      return false;
    }

    const processSchedule = activeLeague?.waiver_type === 'faab_sunday' ? 'Sunday midnight ET' : 'midnight ET tonight';
    toast({ title: 'Waiver claim submitted', description: `$${faabBid} FAAB bid placed. Claims process ${processSchedule}.` });
    await fetchClaims();
    setSubmitting(false);
    return true;
  };

  const cancelClaim = async (claimId: string): Promise<boolean> => {
    if (!activeLeague || !myTeam) return false;
    const { error } = await supabase
      .from('waiver_claims')
      .delete()
      .eq('id', claimId)
      .eq('league_id', activeLeague.id)
      .eq('team_id', myTeam.id);

    if (error) {
      toast({ title: 'Cancel failed', description: error.message, variant: 'destructive' });
      return false;
    }

    toast({ title: 'Claim cancelled' });
    await fetchClaims();
    return true;
  };

  const updateBid = async (claimId: string, newBid: number): Promise<boolean> => {
    if (!activeLeague || !myTeam) return false;
    const { error } = await supabase
      .from('waiver_claims')
      .update({ faab_bid: newBid })
      .eq('id', claimId)
      .eq('league_id', activeLeague.id)
      .eq('team_id', myTeam.id);

    if (error) {
      toast({ title: 'Update failed', description: error.message, variant: 'destructive' });
      return false;
    }

    toast({ title: 'Bid updated', description: `New bid: $${newBid}` });
    await fetchClaims();
    return true;
  };

  const reorderClaims = async (reorderedIds: string[]): Promise<boolean> => {
    if (!activeLeague || !myTeam) return false;
    // Update priority for each claim
    const updates = reorderedIds.map((id, i) =>
      supabase.from('waiver_claims').update({ priority: i + 1 }).eq('id', id).eq('league_id', activeLeague.id).eq('team_id', myTeam.id)
    );

    const results = await Promise.all(updates);
    const hasError = results.some(r => r.error);

    if (hasError) {
      toast({ title: 'Reorder failed', variant: 'destructive' });
      return false;
    }

    await fetchClaims();
    return true;
  };

  return {
    pendingClaims,
    claimHistory,
    loading,
    submitting,
    myTeam,
    submitClaim,
    cancelClaim,
    updateBid,
    reorderClaims,
    fetchClaims,
  };
};